import { useEffect, useState, type ReactNode } from 'react';
import { colour, radius, space, text } from '../../design/tokens.ts';
import { Button, Card, Field, Select, Tag, TextArea, TextInput } from '../../components/basics.tsx';
import { api } from '../../api/client.ts';

/**
 * Editing what a step actually is: one command, or an ordered chain of tools.
 *
 * The thing this screen is really for is the part nobody can hold in their
 * head — which entry can use which value. An entry may only use what the ones
 * before it leave behind, and a mistake there is not discovered until a render
 * machine fails overnight. So the server's own checker runs while you type
 * (`POST /api/admin/processes/check`, which writes nothing) and its answers
 * are shown against the entry they are about, not in a list at the bottom.
 *
 * Three refusals are worth knowing about before they happen:
 *   · a tool the registry has never heard of,
 *   · an entry that hands work on without passing `{{callback_url}}`, which
 *     would wait until it gave up on every single run,
 *   · a value used before anything makes it.
 */

export interface Need {
  name: string;
  from: 'artifact' | 'previous' | 'param' | 'scratch';
  of?: string | undefined;
}

export interface Make {
  name: string;
  path: string;
}

export interface Entry {
  tool: string;
  command: Record<string, string>;
  blocking?: boolean | undefined;
  needs?: Need[] | undefined;
  makes?: Make[] | undefined;
  give_up_after_minutes?: number | undefined;
  says?: string | undefined;
}

export interface ProcessRow {
  name: string;
  label: string;
  accepts?: string[] | undefined;
  produces?: string | undefined;
  command?: Record<string, string> | undefined;
  chain?: Entry[] | undefined;
  default_owner?: string | undefined;
  runnable: boolean;
  sample?: boolean | undefined;
  used_by: string[];
  problems: Problem[];
}

export interface Problem {
  at: string;
  says: string;
  fatal: boolean;
}

export interface ToolChoice {
  name: string;
  label: string;
  install: 'pull' | 'manual';
}

const FROM: { value: Need['from']; label: string }[] = [
  { value: 'previous', label: 'an earlier tool in this chain' },
  { value: 'artifact', label: 'a file Maestro already has' },
  { value: 'param', label: 'a value given when the run starts' },
  { value: 'scratch', label: 'the working folder' }
];

export function ProcessEditor({
  initial,
  tools,
  platforms,
  placeholders,
  onSave,
  onCancel
}: {
  initial: ProcessRow;
  tools: ToolChoice[];
  platforms: string[];
  placeholders: { name: string; says: string }[];
  onSave: (def: ProcessRow) => void;
  onCancel: () => void;
}) {
  const [def, setDef] = useState<ProcessRow>(initial);
  const [problems, setProblems] = useState<Problem[]>(initial.problems ?? []);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const isNew = initial.name === '';

  // Checked as it is typed, by the same function that will refuse the save, so
  // the screen never promises something the server then turns down. Settled
  // for a moment first, because every keystroke of a command would otherwise
  // be a round trip.
  useEffect(() => {
    let dropped = false;
    const timer = setTimeout(() => {
      void api
        .post<{ problems: Problem[] }>('/api/admin/processes/check', def)
        .then((answer) => {
          if (!dropped) setProblems(answer.problems);
        })
        .catch(() => {
          // The checker being unreachable is not a reason to block an edit;
          // the save still goes through the same checks on the server.
          if (!dropped) setProblems([]);
        });
    }, 400);
    return () => {
      dropped = true;
      clearTimeout(timer);
    };
  }, [def]);

  const chain = def.chain ?? [];
  const fatal = problems.filter((p) => p.fatal);
  const at = (where: string): Problem[] => problems.filter((p) => p.at === where);

  const setEntry = (i: number, patch: Partial<Entry>): void =>
    setDef({ ...def, chain: chain.map((e, j) => (i === j ? { ...e, ...patch } : e)) });

  const move = (i: number, by: number): void => {
    const to = i + by;
    if (to < 0 || to >= chain.length) return;
    const next = [...chain];
    [next[i], next[to]] = [next[to]!, next[i]!];
    setDef({ ...def, chain: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.control }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>
        <Field label="Name a pipeline refers to" grow>
          <TextInput
            value={def.name}
            onChange={(name) => setDef({ ...def, name })}
            placeholder="colour_correction"
          />
        </Field>
        <Field label="What people read on the board" grow>
          <TextInput
            value={def.label}
            onChange={(label) => setDef({ ...def, label })}
            placeholder="Colour correction"
          />
        </Field>
      </div>

      {at('name').map((p, i) => (
        <Says key={`n${i}`} problem={p} />
      ))}
      {at('label').map((p, i) => (
        <Says key={`l${i}`} problem={p} />
      ))}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>
        <Field label="It makes" grow>
          <TextInput
            value={def.produces ?? ''}
            onChange={(produces) => setDef({ ...def, produces })}
            placeholder="movie"
          />
        </Field>
        <Field label="It takes (comma separated)" grow>
          <TextInput
            value={(def.accepts ?? []).join(', ')}
            onChange={(v) =>
              setDef({
                ...def,
                accepts: v
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
              })
            }
            placeholder="plate, movie"
          />
        </Field>
        <Field label="Belongs to, unless a pipeline says otherwise" grow>
          <TextInput
            value={def.default_owner ?? ''}
            onChange={(default_owner) => setDef({ ...def, default_owner })}
            placeholder="vfx"
          />
        </Field>
      </div>

      {/* Rule 9's switch. A definition already set this way cannot be turned
          back on — the server refuses — so it is shown as a fact rather than
          a control once it is off. */}
      {def.runnable === false ? (
        <Card>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            Maestro records this step and never runs it. That cannot be changed here: a step that
            runs is a different step, added under another name.
          </span>
        </Card>
      ) : (
        isNew && (
          <Field label="Does Maestro run it?">
            <Select
              value={def.runnable ? 'yes' : 'no'}
              onChange={(v) => setDef({ ...def, runnable: v === 'yes' })}
              options={[
                { value: 'yes', label: 'Yes — Maestro launches it on a workstation' },
                { value: 'no', label: 'No — Maestro only records that somebody did it' }
              ]}
            />
          </Field>
        )
      )}

      {def.runnable !== false && (
        <>
          <Card
            title="The tools it runs, in order"
            aside={
              <Button onClick={() => setShowPlaceholders(!showPlaceholders)}>
                {showPlaceholders ? 'Hide' : 'What can I put in a command?'}
              </Button>
            }
          >
            {showPlaceholders && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {placeholders.map((p) => (
                  <div key={p.name} style={{ display: 'flex', gap: space.tight, flexWrap: 'wrap' }}>
                    <Tag tone="mono">{`{{${p.name}}}`}</Tag>
                    <span style={{ ...text.note, color: colour.inkMuted }}>{p.says}</span>
                  </div>
                ))}
                <span style={{ ...text.note, color: colour.inkMuted }}>
                  Anything a tool leaves behind can be used by the ones after it, under the name it
                  was given.
                </span>
              </div>
            )}
            <span style={{ ...text.small, color: colour.inkSoft }}>
              Most steps are one tool. A chain is for the ones that are not — something that submits
              to a farm and something that collects the result, for instance.
            </span>
            {at('chain').map((p, i) => (
              <Says key={i} problem={p} />
            ))}
          </Card>

          {chain.map((entry, i) => (
            <Card
              key={i}
              title={`${i + 1}. ${tools.find((t) => t.name === entry.tool)?.label ?? (entry.tool || 'Pick a tool')}`}
              aside={
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button onClick={() => move(i, -1)} disabled={i === 0} disabledReason="it is first">
                    ↑
                  </Button>
                  <Button
                    onClick={() => move(i, 1)}
                    disabled={i === chain.length - 1}
                    disabledReason="it is last"
                  >
                    ↓
                  </Button>
                  <Button
                    tone="danger"
                    onClick={() => setDef({ ...def, chain: chain.filter((_, j) => j !== i) })}
                  >
                    Remove
                  </Button>
                </div>
              }
            >
              {at(`chain[${i}]`).map((p, k) => (
                <Says key={k} problem={p} />
              ))}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control }}>
                <Field label="Tool" grow>
                  <Select
                    value={entry.tool}
                    onChange={(tool) => setEntry(i, { tool })}
                    options={[
                      { value: '', label: 'Pick a tool…' },
                      ...tools.map((t) => ({
                        value: t.name,
                        label: `${t.label}${t.install === 'manual' ? ' (installed by hand)' : ''}`
                      }))
                    ]}
                  />
                </Field>
                <Field label="When does it finish?" grow>
                  <Select
                    value={entry.blocking === false ? 'hands_on' : 'finishes'}
                    onChange={(v) =>
                      setEntry(i, {
                        blocking: v === 'finishes',
                        ...(v === 'finishes' ? { give_up_after_minutes: undefined } : {})
                      })
                    }
                    options={[
                      { value: 'finishes', label: 'It finishes on its own and the results are there' },
                      { value: 'hands_on', label: 'It hands the work on and something reports back' }
                    ]}
                  />
                </Field>
                {entry.blocking === false && (
                  <Field label="Give up after (minutes)" grow>
                    <TextInput
                      value={String(entry.give_up_after_minutes ?? '')}
                      onChange={(v) =>
                        setEntry(i, {
                          give_up_after_minutes: Number(v.trim()) > 0 ? Number(v) : undefined
                        })
                      }
                      placeholder="720"
                    />
                  </Field>
                )}
              </div>

              <Field label="Said on screen while this is what the run is doing">
                <TextInput
                  value={entry.says ?? ''}
                  onChange={(says) => setEntry(i, { says })}
                  placeholder="Submitted to the farm, waiting to be told it is done"
                />
              </Field>

              {platforms.map((platform) => (
                <Field key={platform} label={`Command on ${platform}`}>
                  <TextArea
                    value={entry.command?.[platform] ?? ''}
                    onChange={(v) => {
                      const command = { ...(entry.command ?? {}) };
                      if (v.trim() === '') delete command[platform];
                      else command[platform] = v;
                      setEntry(i, { command });
                    }}
                    placeholder={
                      platform === 'windows'
                        ? 'ffmpeg -i {{input}} -vf eq=gamma=1.1 {{output}}'
                        : 'leave empty if it does not run here'
                    }
                  />
                </Field>
              ))}

              <Rows
                label="It needs"
                says="Resolved before the command is filled in. Each of these is usable as {{name}}."
                rows={entry.needs ?? []}
                onAdd={() =>
                  setEntry(i, { needs: [...(entry.needs ?? []), { name: '', from: 'previous' }] })
                }
                onRemove={(k) => setEntry(i, { needs: (entry.needs ?? []).filter((_, j) => j !== k) })}
                render={(need, k) => (
                  <>
                    <Field label="Called" grow>
                      <TextInput
                        value={need.name}
                        onChange={(name) =>
                          setEntry(i, {
                            needs: (entry.needs ?? []).map((n, j) => (j === k ? { ...n, name } : n))
                          })
                        }
                        placeholder="stabilised"
                      />
                    </Field>
                    <Field label="From" grow>
                      <Select
                        value={need.from}
                        onChange={(from) =>
                          setEntry(i, {
                            needs: (entry.needs ?? []).map((n, j) => (j === k ? { ...n, from } : n))
                          })
                        }
                        options={FROM}
                      />
                    </Field>
                    <Field label="Of" grow>
                      <TextInput
                        value={need.of ?? ''}
                        onChange={(of) =>
                          setEntry(i, {
                            needs: (entry.needs ?? []).map((n, j) => (j === k ? { ...n, of } : n))
                          })
                        }
                        placeholder="plate"
                      />
                    </Field>
                  </>
                )}
              />

              <Rows
                label="It leaves behind"
                says="What the tools after it can use, and the path it will be at."
                rows={entry.makes ?? []}
                onAdd={() =>
                  setEntry(i, { makes: [...(entry.makes ?? []), { name: '', path: '' }] })
                }
                onRemove={(k) => setEntry(i, { makes: (entry.makes ?? []).filter((_, j) => j !== k) })}
                render={(made, k) => (
                  <>
                    <Field label="Called" grow>
                      <TextInput
                        value={made.name}
                        onChange={(name) =>
                          setEntry(i, {
                            makes: (entry.makes ?? []).map((m, j) => (j === k ? { ...m, name } : m))
                          })
                        }
                        placeholder="flat"
                      />
                    </Field>
                    <Field label="At" grow>
                      <TextInput
                        value={made.path}
                        onChange={(path) =>
                          setEntry(i, {
                            makes: (entry.makes ?? []).map((m, j) => (j === k ? { ...m, path } : m))
                          })
                        }
                        placeholder="{{scratch}}/flat.exr"
                      />
                    </Field>
                  </>
                )}
              />
            </Card>
          ))}

          <Card>
            <Button
              onClick={() =>
                setDef({
                  ...def,
                  // Moving to a chain and keeping a single command would mean
                  // two answers to what this step runs, and the chain is the
                  // one that wins — so the command comes with it.
                  chain: [
                    ...chain,
                    { tool: '', command: chain.length === 0 ? (def.command ?? {}) : {}, blocking: true }
                  ],
                  ...(chain.length === 0 ? { command: undefined } : {})
                })
              }
            >
              {chain.length === 0 ? 'Make this a chain of tools' : 'Add a tool'}
            </Button>
          </Card>

          {chain.length === 0 &&
            platforms.map((platform) => (
              <Field key={platform} label={`The one command, on ${platform}`}>
                <TextArea
                  value={def.command?.[platform] ?? ''}
                  onChange={(v) => {
                    const command = { ...(def.command ?? {}) };
                    if (v.trim() === '') delete command[platform];
                    else command[platform] = v;
                    setDef({ ...def, command });
                  }}
                  placeholder={
                    platform === 'windows'
                      ? 'ffmpeg -i {{input}} {{output}}'
                      : 'leave empty if it does not run here'
                  }
                />
              </Field>
            ))}
          {at('command').map((p, i) => (
            <Says key={i} problem={p} />
          ))}
        </>
      )}

      <div style={{ display: 'flex', gap: space.tight, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          tone="primary"
          onClick={() => onSave(def)}
          disabled={fatal.length > 0}
          disabledReason={fatal[0]?.says ?? ''}
        >
          Save
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
        {/* Said here as well as against the entry: by the time somebody has
            scrolled a long chain to the bottom, the reason the button is grey
            is off the screen, and a tooltip is no answer on a touch screen. */}
        {fatal.length > 0 ? (
          <span style={{ ...text.note, color: colour.staleInk }}>
            {fatal.length === 1
              ? fatal[0]!.says
              : `${fatal.length} things would stop a run, the first being: ${fatal[0]!.says}`}
          </span>
        ) : (
          problems.length > 0 && (
            <span style={{ ...text.note, color: colour.inkMuted }}>
              {problems.length === 1
                ? 'One thing worth a look'
                : `${problems.length} things worth a look`}
              , none of which stops it being saved.
            </span>
          )
        )}
      </div>
    </div>
  );
}

/** One of the checker's answers, where it belongs. */
function Says({ problem }: { problem: Problem }) {
  return (
    <div
      style={{
        ...text.small,
        display: 'flex',
        gap: space.tight,
        padding: '8px 10px',
        borderRadius: radius.control,
        border: `1px solid ${problem.fatal ? colour.stale : colour.edge}`,
        background: problem.fatal ? colour.staleWash : colour.surfaceSunk,
        color: colour.ink
      }}
    >
      <strong style={{ ...text.monoStrong, color: problem.fatal ? colour.stale : colour.inkMuted }}>
        {problem.fatal ? 'stops a run' : 'worth a look'}
      </strong>
      <span>{problem.says}</span>
    </div>
  );
}

/** A small repeated group — needs, makes — with its own add and remove. */
function Rows<T>({
  label,
  says,
  rows,
  onAdd,
  onRemove,
  render
}: {
  label: string;
  says: string;
  rows: T[];
  onAdd: () => void;
  onRemove: (i: number) => void;
  render: (row: T, i: number) => ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.tight }}>
      <div style={{ display: 'flex', gap: space.tight, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ ...text.label, color: colour.inkMuted }}>{label}</span>
        <span style={{ ...text.note, color: colour.inkMuted }}>{says}</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: space.tight, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {render(row, i)}
          <Button onClick={() => onRemove(i)}>Remove</Button>
        </div>
      ))}
      <Button onClick={onAdd} style={{ alignSelf: 'flex-start' }}>
        Add
      </Button>
    </div>
  );
}
